import React, { useEffect, useState } from "react";
import { Button } from "@/components/Button";
import { QuantitySelector } from "../../QuantitySelector";
import { IntervalSelector } from "../../IntervalSelector";
import { addMonthsToDate } from "@/helpers/utils";
import { DrawerPageProps } from "../types";
import { ProductCard } from "@/features/product/ProductCard";
import { Unit } from "@/helpers/Unit";
import { RadioButtons } from "@/components/RadioButtons";
import { Cadence } from "../../types";
import { droppsProductToProductCardProduct } from "../../helpers";
import { DroppsProduct, productSelect, variantSelect } from "@/store";
import { AddBoxItemParams } from "../../AccountContext";
import { Money } from "@/helpers/Money";
import { ToggleButton } from "@/components/ToggleButton";
import { DroppsVariant } from "@/store";

interface ConfirmItemChangePageProps {
  product: DroppsProduct;
  onChange: (item: AddBoxItemParams) => void;
  nextChargeDate: Date;
  initialQuantity?: number;
  cadence?: Cadence;
  displayMode?: "onlySpot" | "onlySubscription" | "all";
  canUpdateInterval?: boolean;
}

const ConfirmItemPage = ({
  product,
  onChange,
  nextChargeDate,
  initialQuantity = 1,
  displayMode = "all",
  cadence = { frequency: 4, unit: new Unit("month") },
  canUpdateInterval = false,
}: ConfirmItemChangePageProps & DrawerPageProps) => {
  const [quantity, setQuantity] = useState(initialQuantity);
  const [newShipInterval, setNewShipInterval] = useState(cadence?.frequency);
  const variant = productSelect.defaultVariant(product);
  const subscription = variantSelect.orderableSubscription(variant);
  const [isSubscription, setIsSubscription] = useState(
    displayMode !== "onlySpot" && !!subscription
  );
  const newCadence = isSubscription
    ? { ...cadence, frequency: newShipInterval }
    : undefined;
  const price = isSubscription
    ? variantSelect.lowestPrice(variant)
    : variant.price;

  const discount = product.sellingPlans[0].discount;
  const [size, setSize] = useState(variant.package_quantity + " count");

  // Function to calculate discounted price
  const getDiscountedPrice = (price: Money, discount: any) => {
    if (discount.kind === "percentage") {
      const discountedCents = price.asCents() * (1 - discount.percentage / 100);
      return Money.from(discountedCents, "¢");
    } else if (discount.kind === "fixed") {
      const discountedCents = price.asCents() - discount.offset.asCents();
      return Money.from(discountedCents, "¢");
    } else if (discount.kind === "price") {
      return discount.price;
    } else {
      return price;
    }
  };

  const [variantPrice, setVariantPrice] = useState(() => {
    return getDiscountedPrice(variant.price, discount);
  });

  const [newVariant, setNewVariant] = useState(variant.id);
  const selectedVariant =
    product.variants.find((v) => v.id === newVariant) ??
    productSelect.defaultVariant(product);

  useEffect(() => {
    const selectedVariant = product.variants.find((v) => v.id === newVariant);
    if (selectedVariant) {
      const discountedPrice = getDiscountedPrice(
        selectedVariant.price,
        product.sellingPlans[0].discount
      );
      setVariantPrice(discountedPrice);
      setSize(selectedVariant.package_quantity + " count");
    }
  }, [newVariant, product.variants, product.sellingPlans]);

  const handleShipIntervalChange = (value: number | null) => {
    if (value) setNewShipInterval(value);
  };

  const handleQuantityChange = (value: number | null) => {
    if (value) setQuantity(value);
  };

  const subOptions =
    displayMode === "all" && subscription
      ? [
          {
            value: "subscription",
            label: (
              <>
                <p className="body text-sm font-semibold">Subscription</p>
                <p className="body text-sm">
                  {variantPrice.mul(quantity).format()}
                </p>
              </>
            ),
          },
          {
            value: "spot",
            label: (
              <>
                <p className="body text-sm font-semibold">One-time purchase</p>
                <p className="body text-sm">
                  {selectedVariant.price.mul(quantity).format()}
                </p>
              </>
            ),
          },
        ]
      : [];
  const handleSubSelection = (
    e: React.ChangeEvent<HTMLInputElement>,
    value: string
  ) => {
    setIsSubscription(value !== "spot");
  };

  const productCardProduct = {
    ...droppsProductToProductCardProduct({ product, variant }),
    price: price.mul(quantity),
  };
  const upcomingDateStrings = new Array(3).fill(0).map((x, i) => {
    return newShipInterval
      ? addMonthsToDate(nextChargeDate, newShipInterval * i).toLocaleDateString(
          "en-us",
          {
            year: "numeric",
            month: "long",
            day: "numeric",
            timeZone: "UTC",
          }
        )
      : "";
  });

  const handleVariantChange = (value: number | null) => {
    if (value !== null) {
      const variant = product.variants.find((v) => v.id === value);
      if (variant) {
        setSize(selectedVariant.package_quantity + " count");
        setNewVariant(value);
      }
    }
  };

  const VariantButton = ({
    variant,
    className,
  }: {
    variant: DroppsVariant;
    className?: string;
  }) => {
    // Apply 'bg-primary-50' only if this variant is the one currently selected
    const isSelected = variant.id === newVariant;
    return (
      <div
        data-id="variants-button"
        key={variant.id}
        className={`flex-1 ${isSelected ? "bg-primary-50" : ""} ${className}`}
      >
        {variant?.quantity_discount !== undefined &&
          variant.quantity_discount > 0 && (
            <p
              className={`buy-more-save-more-label ${
                isSelected ? "bg-primary-800" : ""
              }`}
            >
              Save {variant.quantity_discount}%
            </p>
          )}

        <ToggleButton
          className="w-full"
          selected={isSelected}
          size="md"
          onClick={() => handleVariantChange(variant.id)}
          label={
            <span className="text-base">
              {variantSelect.sizeFormat(variant)}
            </span>
          }
          name="variant-button"
        />
      </div>
    );
  };
  productCardProduct.size = "";
  return (
    <>
      <h3 className="subtitle mb-4 text-base font-semibold">Select Product</h3>
      <ProductCard
        productCardValue={{ ...productCardProduct, price: undefined }}
        compact={true}
        hoverable={false}
        bordered
      />
      {/* add variant button here */}
      <div className="w-full">
        <h3 className="subtitle mb-4 text-base font-semibold">Change Size</h3>

        <div
          role="radiogroup"
          className="flex gap-2"
          aria-label={`Select product variant`}
        >
          {product.variants.map((x) => (
            <VariantButton
              key={variantSelect.reactKey(x)}
              variant={x}
              className={""}
            />
          ))}
        </div>
      </div>
      <QuantitySelector
        value={quantity}
        label="Confirm Quantity"
        price={variantPrice}
        size={`${selectedVariant.package_quantity} Count`}
        onChange={handleQuantityChange}
      />
      {subOptions.length > 0 && (
        <RadioButtons
          options={subOptions}
          value={isSubscription ? "subscription" : "spot"}
          onChange={handleSubSelection}
          row
        />
      )}
      {isSubscription && canUpdateInterval && (
        <div className="w-full space-y-4">
          <IntervalSelector
            label="Confirm Ship Frequency"
            value={newShipInterval}
            onChange={handleShipIntervalChange}
          />
          <p className="body text-sm">
            Based on the frequency selected above, the next three ship dates are
            scheduled for:
          </p>
          <ul className="ml-5 text-sm">
            {upcomingDateStrings.map((dateString) => {
              return (
                <li key={dateString} className="list-disc">
                  {dateString}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <Button
        className="w-full"
        onClick={() =>
          onChange({
            product,
            isSubscription,
            cadence: newCadence,
            quantity: quantity,
            shopifyVariantId: newVariant,
          })
        }
      >
        Confirm Product
      </Button>
    </>
  );
};

export { ConfirmItemPage };
